import { styled } from '@linaria/react';
import { Group, Switch, TextInput } from '@mantine/core';
import { useCallback, useMemo, useState } from 'react';
import { useAuditLogSparkline } from '../../../../api/data-management-client/audit-logs-client.hooks';
import { useTranslation } from 'react-i18next';
import { IntegrationType } from '../../../../api/integrations-client/integrations-client.type';
import { Sparklines } from '../../../../components/audit-log/sparklines';
import { primaryBase, skyDark } from '../../../../styles/design-tokens';
import { Divider } from '../../../../ui-library/divider/divider';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { DataManagementDropShadowContainer } from '../data-management-header';
import { Table, TableColumn } from '../table';
import { DataSourceTableHeader } from './data-source-table-header';
import { DataSourcesActionButton } from './data-sources-action-button';
import { createIntegrationTypeData, filterTableData } from './data-sources-panel.helpers.tsx';
import type { DataSourcesPanelProps, DataSourceTableRow } from './data-sources-panel.type';
import { isProjectOrBoardActive } from './data-sources.helpers.ts';

export function DataSourcesPanel({
  selectedDataSource,
  integrationEntries,
  selectedProjectId,
  selectedSubprojectId,
  setSelectedProjectId,
  setSelectedSubprojectId,
}: DataSourcesPanelProps) {
  const i18n = useTranslation();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [refreshStatuses, setRefreshStatuses] = useState<Record<string, number>>({});
  const [showIntegratedOnly, setShowIntegratedOnly] = useState(false);

  const integrationTypeData = createIntegrationTypeData(integrationEntries, expandedRows);

  // Collect distinct project ids for batch sparkline fetching. Uses
  // `row.linkedProjectId` (the linked project id) rather than `row.id`, since
  // repo-style rows carry the repo's own id in `row.id`, which the
  // project-keyed audit-log endpoint cannot resolve. Rows without a linked
  // project are skipped — they continue to render the existing "No data"
  // placeholder. Deduped via Set: multiple repos that link to the same
  // backend project (a common GitHub pattern) would otherwise inflate the
  // query string and the React Query cache key.
  const allProjectIds = useMemo(() => {
    const projectIds = new Set<string>();
    Object.values(integrationTypeData).forEach((rows) => {
      rows.forEach((row) => {
        if (!row.isBoard && row.linkedProjectId) {
          projectIds.add(row.linkedProjectId);
        }
      });
    });
    return Array.from(projectIds);
  }, [integrationTypeData]);

  // Fetch sparkline data
  const { data: sparklineData, query } = useAuditLogSparkline({
    project_ids: allProjectIds,
  });
  const { isLoading: sparklinesLoading, error: sparklinesError } = query;

  // Apply search filtering to each integration type
  const filteredData = useMemo(() => {
    const filtered: Record<IntegrationType, DataSourceTableRow[]> = {} as Record<IntegrationType, DataSourceTableRow[]>;
    Object.entries(integrationTypeData).forEach(([type, rows]) => {
      let typeRows = filterTableData(rows, search);
      // Filter to show only integrated (non-placeholder date) rows when toggle is on
      if (showIntegratedOnly) {
        typeRows = typeRows.filter((row) => row.lastUpdated !== '-');
      }
      filtered[type as IntegrationType] = typeRows;
    });
    return filtered;
  }, [integrationTypeData, search, showIntegratedOnly]);

  const getIntegrationEntryForRow = useCallback(
    (row: DataSourceTableRow) => {
      // Match by project id, subproject id, OR repository id so SCM-style
      // entries (e.g. GitHub) — whose rows carry `repo.id` — also resolve to
      // their owning entry, otherwise the action column renders null.
      return integrationEntries.find(
        (entry) =>
          entry.projects.some((project) => project.id === row.id) ||
          entry.subprojects.some((subproject) => subproject.id === row.id) ||
          (entry.repositories?.some((repo) => repo.id === row.id) ?? false),
      );
    },
    [integrationEntries],
  );

  const handleRefreshSuccess = useCallback((integrationId: string, status: number) => {
    setRefreshStatuses((prev) => ({ ...prev, [integrationId]: status }));
  }, []);

  const handleDropdownClick = (row: DataSourceTableRow) => {
    if (row.isBoard) return;
    setSelectedProjectId(null);
    setSelectedSubprojectId(null);
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(row.key)) {
        newSet.delete(row.key);
      } else {
        newSet.add(row.key);
      }
      return newSet;
    });
  };

  const handleRowClick = (row: DataSourceTableRow) => {
    // `row.id` (not `row.linkedProjectId`) is intentional here — this drives
    // row-selection identity and entry matching (`getIntegrationEntryForRow`),
    // both of which key on the row's own id (`repo.id` for GitHub rows).
    // Swapping to `linkedProjectId` would break selection toggle when multiple
    // repos share a linked project and would break the action column lookup.
    if (row.isBoard) {
      setSelectedProjectId(null);
      setSelectedSubprojectId(selectedSubprojectId === row.id ? null : row.id);
    } else {
      setSelectedSubprojectId(null);
      setSelectedProjectId(selectedProjectId === row.id ? null : row.id);
    }

    setSelectedRecordId(row.key);
  };

  const toggleSection = (type: string) => {
    setCollapsedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };

  const columns: TableColumn<DataSourceTableRow>[] = [
    {
      key: 'projectName',
      label: 'Project Key-Name',
      width: '25%',
      render: (_value: DataSourceTableRow[keyof DataSourceTableRow], row: DataSourceTableRow) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 8 }}>
          {!row.isBoard ? (
            <>
              <div
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  handleDropdownClick(row);
                }}
                style={{ display: 'flex', cursor: 'pointer' }}
              >
                <Icon name={row.isExpanded ? 'expand_more' : 'chevron_right'} size={20} />
              </div>
              <Icon
                name="circle"
                variant="filled"
                size={18}
                color={isProjectOrBoardActive(row.lastUpdated) ? primaryBase : skyDark}
              />
              <Text weight="medium">{row.projectName}</Text>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 48 }}>
              <Icon
                name="circle"
                variant="filled"
                size={18}
                color={isProjectOrBoardActive(row.lastUpdated) ? primaryBase : skyDark}
              />
              <Text weight="medium">{row.projectName}</Text>
            </div>
          )}
        </div>
      ),
      sortable: true,
    },
    { key: 'totalBoards', label: 'Total Boards', width: '10%', sortable: true },
    { key: 'owner', label: 'Owner', width: '10%', sortable: true },
    { key: 'lastUpdated', label: 'Last Updated', width: '12%', sortable: true },
    {
      key: 'upToDate',
      label: 'Up to Date',
      width: '10%',
      sortable: true,
      render: (_value: DataSourceTableRow[keyof DataSourceTableRow], row: DataSourceTableRow) => row.upToDate,
    },
    {
      key: 'activity' as keyof DataSourceTableRow,
      label: 'Activity',
      width: '12%',
      sortable: false,
      render: (_value: DataSourceTableRow[keyof DataSourceTableRow], row: DataSourceTableRow) => {
        if (row.isBoard) return null;

        // Extract project name from ReactNode
        const projectName = typeof row.projectName === 'string' ? row.projectName : row.id;

        return (
          <Sparklines
            projectName={projectName}
            width={80}
            height={25}
            showTotal={false}
            data={row.linkedProjectId ? sparklineData?.[row.linkedProjectId] : undefined}
            isLoading={sparklinesLoading}
            error={sparklinesError}
          />
        );
      },
    },
    {
      key: 'action',
      label: 'Action',
      width: '11%',
      sortable: false,
      render: (_value: DataSourceTableRow[keyof DataSourceTableRow], row: DataSourceTableRow) => {
        const integrationEntry = getIntegrationEntryForRow(row);
        if (!integrationEntry || row.isBoard) return null;

        return (
          <DataSourcesActionButton
            integrationEntry={integrationEntry}
            refreshStatus={refreshStatuses[integrationEntry.id]}
            onRefreshSuccess={(status: number) => handleRefreshSuccess(integrationEntry.id, status)}
          />
        );
      },
    },
  ];

  const renderTable = (data: DataSourceTableRow[], isAlreadyFiltered = false) => {
    // Only filter if data hasn't been filtered yet (when selectedDataSource is set)
    // When isAlreadyFiltered=true, data comes from filteredData which already has search and showIntegratedOnly applied
    let tableData = data;
    if (!isAlreadyFiltered) {
      tableData = filterTableData(data, search);
      // Apply integrated-only filter only when not already filtered
      if (showIntegratedOnly) {
        tableData = tableData.filter((row) => row.lastUpdated !== '-');
      }
    }

    return (
      <Table
        data={tableData}
        columns={columns}
        selectedRowId={selectedRecordId || undefined}
        onRowClick={handleRowClick}
        getRowId={(row) => row.key}
        stickyHeader
        isChildRow={(row) => Boolean(row.isBoard)}
        getParentId={(row) => row.parentId || ''}
      />
    );
  };

  return (
    <DataManagementDropShadowContainer>
      <HeaderContainer>
        <Text weight="bold" size="large">
          Data Sources
        </Text>
        <Group>
          <Switch
            label={i18n.t('data_management.data_sources.integrated_only')}
            checked={showIntegratedOnly}
            onChange={(event) => setShowIntegratedOnly(event.currentTarget.checked)}
            size="xs"
          />
          <TextInput
            placeholder="Search for Project or Board"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            leftSection={<Icon name="search" size={16} color={skyDark} />}
            style={{ width: 250 }}
            radius="xl"
            size="xs"
          />
        </Group>
      </HeaderContainer>
      <Divider style={{ marginTop: 24 }} />
      <TablesContainer>
        {selectedDataSource ? (
          <TableSection>
            <DataSourceTableHeader
              type={selectedDataSource}
              isCollapsed={collapsedSections.has(selectedDataSource)}
              onToggle={() => toggleSection(selectedDataSource)}
            />
            {!collapsedSections.has(selectedDataSource) && (
              <TableContainer>{renderTable(integrationTypeData[selectedDataSource] || [], false)}</TableContainer>
            )}
          </TableSection>
        ) : (
          Object.entries(filteredData).map(([type, data]) => (
            <TableSection key={type}>
              <DataSourceTableHeader
                type={type as IntegrationType}
                isCollapsed={collapsedSections.has(type)}
                onToggle={() => toggleSection(type)}
              />
              {!collapsedSections.has(type) && (
                <TableContainer>{renderTable(data as DataSourceTableRow[], true)}</TableContainer>
              )}
              {Object.keys(filteredData).indexOf(type) !== Object.keys(filteredData).length - 1 && (
                <Divider style={{ marginTop: 16, marginBottom: 16 }} />
              )}
            </TableSection>
          ))
        )}
      </TablesContainer>
    </DataManagementDropShadowContainer>
  );
}

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const TablesContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: auto;
  margin-top: 16px;
`;

const TableSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
`;
